import { useEffect, useState } from 'react'
import { supabase, readableError } from '../lib/supabase'
import { Shell } from './Shell'
import { Spinner } from './ui'

const LEVELS = [
  ['learning', 'Learning it'],
  ['comfortable', 'Comfortable'],
  ['lead', 'Could lead it Sunday'],
]

/**
 * Pick from a fixed vocabulary, then say how strongly. Tapping a skill adds it
 * at "comfortable" — the common case takes one tap, and the level is there for
 * anyone who wants to be precise.
 */
export default function SkillPicker({ userId, chosen, onDone, onBack }) {
  const [catalog, setCatalog] = useState(null)
  const [picked, setPicked] = useState(
    () => new Map((chosen ?? []).map((s) => [s.skill_id, s.level]))
  )
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    supabase.from('skill_catalog').select('*').order('kind').order('position')
      .then(({ data }) => setCatalog(data ?? []))
  }, [])

  const toggle = (id) => {
    const next = new Map(picked)
    if (next.has(id)) next.delete(id)
    else next.set(id, 'comfortable')
    setPicked(next)
  }

  const setLevel = (id, level) => {
    const next = new Map(picked)
    next.set(id, level)
    setPicked(next)
  }

  const save = async () => {
    setBusy(true); setErr(null)
    // Replace wholesale — simpler and atomic enough for a set this small.
    const { error: delErr } = await supabase.from('user_skills').delete().eq('user_id', userId)
    if (delErr) { setBusy(false); return setErr(readableError(delErr)) }

    if (picked.size) {
      const rows = [...picked].map(([skill_id, level]) => ({ user_id: userId, skill_id, level }))
      const { error } = await supabase.from('user_skills').insert(rows)
      if (error) { setBusy(false); return setErr(readableError(error)) }
    }
    setBusy(false)
    onDone()
  }

  if (!catalog) return <Shell tabs><Spinner /></Shell>

  const section = (kind, title, blurb) => {
    const items = catalog.filter((c) => c.kind === kind)
    return (
      <>
        <div className="eyebrow" style={{ marginTop: 22 }}>{title}</div>
        <p className="small" style={{ marginBottom: 10 }}>{blurb}</p>
        <div className="chips">
          {items.map((c) => (
            <button key={c.id} className={`chip${picked.has(c.id) ? ' on' : ''}`}
                    onClick={() => toggle(c.id)}>{c.label}</button>
          ))}
        </div>
        {items.filter((c) => picked.has(c.id)).map((c) => (
          <div key={c.id} className="levelrow">
            <span className="levelname">{c.label}</span>
            <div className="levels">
              {LEVELS.map(([id, label]) => (
                <button key={id} className={`lvl${picked.get(c.id) === id ? ' on' : ''}`}
                        onClick={() => setLevel(c.id, id)}>{label}</button>
              ))}
            </div>
          </div>
        ))}
      </>
    )
  }

  return (
    <Shell tabs>
      <button className="backlink" onClick={onBack}>‹ Profile</button>
      <h2 className="q">What do you bring?</h2>
      <p className="sub">
        This is how a pastor knows who to put you with. Nothing here is a boast —
        "learning it" is a useful thing to say.
      </p>

      {section('instrument', 'What you play', 'Tap anything you can play, then say how confidently.')}
      {section('competency', "What you're good at", 'Writing, production, the desk — whatever you actually do.')}

      <div className="hr" />
      <button className="btn btn-primary" disabled={busy} onClick={save}>
        {busy ? 'Saving…' : `Save ${picked.size} thing${picked.size === 1 ? '' : 's'}`}
      </button>
      {err && <p className="err">{err}</p>}
    </Shell>
  )
}
